import { FunctionComponent, PropsWithChildren, useCallback, useMemo, useState, MouseEvent, ReactNode } from "react";

import { Dialog, DialogContent, DialogTitle, DialogProps as MuiDialogProps } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";

import { useMediaQuery } from "../../../common/hooks/useMediaQuery";
import { useTranslate } from "../../../common/hooks/useTranslate";
import { DashboardButton } from "../DashboardButton/DashboardButton";
import { DashboardTooltip } from "../DashboardTooltip/DashboardTooltip";
import { ActionProps, AdditionalActionProps } from "../Dialog";
import { DashboardDrawer } from "../Drawer";

import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { Cross as Close } from "@/icons/outlined";

type NewModalProps = {
  actions?: {
    main?: ActionProps;
    cancel?: ActionProps;
    additional?: AdditionalActionProps[];
  };
  onClose?: () => void;
  isOpen: boolean;
  title?: ReactNode;
  noDivider?: boolean;
  // Adds a top border to the desktop footer, hinting that content may scroll behind it.
  footerDivider?: boolean;
  width?: "small" | "medium" | "large";
  scrollableContent?: boolean;
  // Desktop only — e.g. set 0 to swap seamlessly with another modal without a backdrop flicker.
  transitionDuration?: MuiDialogProps["transitionDuration"];
};

export const DashboardAdaptiveModal: FunctionComponent<PropsWithChildren<NewModalProps>> = ({
  children,
  actions,
  onClose,
  isOpen,
  title,
  noDivider,
  footerDivider = false,
  width = "small",
  scrollableContent = false,
  transitionDuration,
}) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      setIsSubmitting(true);
      await actions?.main?.onClick?.(event);
      setIsSubmitting(false);
    },
    [actions?.main]
  );

  const closeModal = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const header = (
    <Grid container alignItems="center">
      <Grid item xs>
        {title}
      </Grid>
      {!isSmallScreen && (
        <Grid item>
          <DashboardIconButton onClick={onClose} sx={{ cursor: "pointer", zIndex: 10 }} vibe="neutral">
            <Close />
          </DashboardIconButton>
        </Grid>
      )}
    </Grid>
  );

  const displayCancelButton = actions?.cancel && !actions.cancel.hidden;
  const displayMainButton = actions?.main && !actions.main.hidden;

  const mainButton = useMemo(() => {
    if (!actions?.main || !displayMainButton) {
      return null;
    }
    const { tooltip, isLoading, ...buttonProps } = actions.main;
    const button = (
      <DashboardButton
        variant="filled"
        vibe="brand"
        {...buttonProps}
        onClick={handleSubmit}
        isLoading={isSubmitting || isLoading}
        fullWidth
      >
        {actions.main.children || t("dashboard", "common.confirm")}
      </DashboardButton>
    );
    if (tooltip) {
      return (
        <DashboardTooltip title={tooltip.title} placement={tooltip.placement}>
          <Grid item>{button}</Grid>
        </DashboardTooltip>
      );
    }
    return <Grid item>{button}</Grid>;
  }, [actions?.main, displayMainButton, handleSubmit, isSubmitting, t]);

  const cancelButton = useMemo(() => {
    if (!actions?.cancel || !displayCancelButton) {
      return null;
    }
    const { tooltip, ...buttonProps } = actions.cancel;

    const button = (
      <DashboardButton variant="outlined" vibe="neutral" {...buttonProps} fullWidth>
        {actions.cancel.children || t("dashboard", "common.cancel")}
      </DashboardButton>
    );

    if (tooltip) {
      return (
        <DashboardTooltip title={tooltip.title} placement={tooltip.placement}>
          <Grid item>{button}</Grid>
        </DashboardTooltip>
      );
    }
    return <Grid item>{button}</Grid>;
  }, [actions?.cancel, displayCancelButton, t]);

  const additionalButtons = actions?.additional?.map((buttonProps) => (
    <Grid item key={buttonProps.children?.toString()}>
      <DashboardButton {...buttonProps} fullWidth />
    </Grid>
  ));

  const customContent = (
    <Grid
      container
      sx={{
        justifyContent: "center",
        alignItems: "center",
        ...(scrollableContent && { flexDirection: "column", alignItems: "stretch", minHeight: 0, flex: 1 }),
      }}
    >
      {children}
    </Grid>
  );

  if (isSmallScreen) {
    return (
      <DashboardDrawer onClose={onClose} open={isOpen} anchor="bottom" title={header} noDivider={noDivider}>
        <Grid container sx={{ alignItems: "space-between", height: "100%" }}>
          <Grid item xs={12} sx={{ marginBottom: 2 }}>
            {children}
          </Grid>
          <Grid item xs={12} alignSelf="flex-end">
            <Grid container justifyContent="flex-end">
              <Grid item xs={isSmallScreen ? 12 : undefined}>
                {additionalButtons}
              </Grid>
              <Grid item sx={{ marginBottom: 1 }} xs={12}>
                {mainButton}
              </Grid>
              <Grid item xs={12}>
                {cancelButton}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DashboardDrawer>
    );
  }

  const maxWidth = width === "small" ? "xs" : width === "medium" ? "sm" : "md";
  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth={maxWidth}
      onClose={closeModal}
      transitionDuration={transitionDuration}
      data-test="new-form-modal"
    >
      <DialogTitle>{header}</DialogTitle>
      <DialogContent
        sx={{
          ...(scrollableContent && { display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }),
          // Removes the bottom gap so scrolling content is clipped exactly at the divider.
          ...(footerDivider && { paddingBottom: 0 }),
        }}
      >
        {customContent}
      </DialogContent>
      <DialogActions
        sx={(theme) => ({
          padding: theme.spacing(2),
          ...(footerDivider && { borderTop: `1px solid ${theme.palette.border.neutral.quiet}` }),
        })}
      >
        <Grid container justifyContent="flex-end">
          {additionalButtons && <Grid item>{additionalButtons}</Grid>}
          <Grid item sx={{ marginRight: 1 }}>
            {cancelButton}
          </Grid>
          <Grid item>{mainButton}</Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};
